/**
 * AuthShowcaseCarousel.tsx
 *
 * The right-panel benefit carousel extracted from example/mvp/login.html.
 * Manages slide state internally.  Receives slide data as props so it
 * stays reusable and testable without any auth logic.
 *
 * Junior devs: to add or remove benefit slides, edit the SHOWCASE_SLIDES
 * array in AuthLayout.tsx — do NOT edit this file.
 */

import { useState } from "react";

export interface ShowcaseSlide {
  img: string;
  brandLogo: string;
  brandAlt: string;
  invert: boolean;
  caption: string;
}

interface AuthShowcaseCarouselProps {
  slides: ShowcaseSlide[];
}

export function AuthShowcaseCarousel({ slides }: AuthShowcaseCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  const prev = () =>
    setActiveIndex((i) => (i === 0 ? slides.length - 1 : i - 1));
  const next = () =>
    setActiveIndex((i) => (i === slides.length - 1 ? 0 : i + 1));

  return (
    <div
      className="auth-showcase__carousel"
      role="region"
      aria-roledescription="carousel"
      aria-label="No-cost member benefits"
    >
      <div className="auth-showcase__stage">
        <button
          type="button"
          className="auth-showcase__arrow"
          onClick={prev}
          aria-label="Previous benefit"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M14.5 6 L8.5 12 L14.5 18" />
          </svg>
        </button>

        <ul className="auth-showcase__deck">
          {slides.map((s, i) => (
            <li
              key={s.brandAlt}
              className={[
                "auth-showcase__slide",
                i === activeIndex ? "auth-showcase__slide--active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-hidden={i !== activeIndex}
            >
              <figure className="auth-showcase__card">
                <img
                  className="auth-showcase__img"
                  src={s.img}
                  alt=""
                  loading="lazy"
                />
                <span className="auth-showcase__scrim" aria-hidden="true" />
                <span className="auth-showcase__pill">Free</span>
                <figcaption className="auth-showcase__body">
                  <span
                    className={[
                      "auth-showcase__brand",
                      s.invert ? "auth-showcase__brand--invert" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <img src={s.brandLogo} alt={s.brandAlt} />
                  </span>
                  <p className="auth-showcase__caption">{s.caption}</p>
                </figcaption>
              </figure>
            </li>
          ))}
        </ul>

        <button
          type="button"
          className="auth-showcase__arrow"
          onClick={next}
          aria-label="Next benefit"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9.5 6 L15.5 12 L9.5 18" />
          </svg>
        </button>
      </div>

      <p className="auth-showcase__count" aria-live="polite">
        {activeIndex + 1} of {slides.length}
      </p>
    </div>
  );
}
